
import React from 'react';
import { SparklesCore } from './ui/sparkles';
import { NoiseBackground } from './ui/noise-background';
import { Language } from '../types';
import { translations } from '../translations';

interface HeroProps {
  lang: Language;
}

const Hero: React.FC<HeroProps> = ({ lang }) => {
  const t = translations[lang].hero;

  const TitleBlock = ({ className = "" }: { className?: string }) => (
    <div className={`relative inline-block w-full text-center md:text-left ${className}`}>
      <h1 className="text-6xl md:text-8xl font-bold leading-none tracking-tighter gradient-text relative z-20 inline-block">
      AxiomityFX
      </h1>
      <div className="w-full h-16 relative mt-2 flex justify-center md:justify-start">
        <div className="relative w-full max-w-[400px]">
          <div className="absolute inset-x-0 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-[2px] w-full blur-sm mx-auto" />
          <div className="absolute inset-x-0 top-0 bg-gradient-to-r from-transparent via-sky-500 to-transparent h-px w-full mx-auto" />
          <div className="absolute inset-x-0 top-0 bg-gradient-to-r from-transparent via-cyan-400 to-transparent h-[5px] w-1/2 blur-sm mx-auto" />
          <div className="absolute inset-x-0 top-0 bg-gradient-to-r from-transparent via-cyan-400 to-transparent h-px w-1/2 mx-auto" />
          <SparklesCore id={`sparkles-${className}`} background="transparent" minSize={0.4} maxSize={1.2} particleDensity={1400} className="w-full h-full" particleColor="#FFFFFF" />
          <div className="absolute inset-0 w-full h-full bg-black [mask-image:radial-gradient(180px_80px_at_top,transparent_20%,white)]"></div>
        </div>
      </div>
    </div>
  );

  return (
    <section id="home" className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden bg-black">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:grid md:grid-cols-[1.1fr_0.9fr] gap-4 lg:gap-8 items-center">
        <TitleBlock className="md:hidden order-1" />
        <div className="relative group flex justify-center items-center order-2 md:order-1 w-full">
          {/* Background glow removed for a pure black aesthetic */}
          <div className="relative z-10 w-full scale-110 md:scale-125 transform hover:scale-115 md:hover:scale-130 transition-transform duration-700 ease-out">
            <img src="/product-box.png" alt="Expert Advisor - Automated Trading Software MT4" className="w-full h-auto object-contain drop-shadow-[0_50px_50px_rgba(0,0,0,0.8)]" />
          </div>
        </div>
        <div className="flex flex-col items-center md:items-start space-y-8 order-3 md:order-2 md:pl-8">
          <TitleBlock className="hidden md:block" />
          <p className="text-xl md:text-2xl text-gray-400 max-w-lg leading-relaxed pt-2 text-center md:text-left font-light">
            {t.description}
          </p>
          <div className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto items-center md:items-start">
            <NoiseBackground containerClassName="w-full sm:w-fit p-[2px] rounded-full" gradientColors={["rgb(14, 165, 233)", "rgb(34, 211, 238)", "rgb(2, 132, 199)"]}>
              <a href="#pricing" className="flex items-center justify-center h-full w-full cursor-pointer rounded-full bg-gradient-to-r from-neutral-100 via-neutral-100 to-white px-10 py-4 text-black font-bold shadow-[0px_2px_0px_0px_rgba(255,255,255,0.5)_inset] transition-all duration-100 active:scale-95 text-center min-w-[200px]">{t.buy}</a>
            </NoiseBackground>
            <a href="#performance" className="px-10 py-4 bg-white/5 border border-white/10 text-white font-bold rounded-full transition-all duration-300 hover:bg-white/10 hover:border-white/30 hover:scale-105 text-center w-full sm:w-auto min-w-[200px]">{t.results}</a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
